import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// --- IMPORTACIÓN DE COMPONENTES MODULARES ---
import FiltrosSidebar from './filtros/FiltrosSidebar';
import TablePreview from './vistas/TablePreview';
import GroupedPreview from './vistas/GroupedPreview';
import KpisPolizas from './kpis/KpisPolizas';
import RendimientoVendedores from './vendedores/RendimientoVendedores';
import RendimientoRenovacionesVendedores from './vendedores/RendimientoRenovacionesVendedores';
import RendimientoCobradores from './vendedores/RendimientoCobradores';
import KpisCobranzas from './kpis/KpisCobranzas';
import KpisAnulaciones from './kpis/KpisAnulaciones';
import KpisEndosos from './kpis/KpisEndosos';
import KpisRefacturaciones from './kpis/KpisRefacturaciones';
import KpisSiniestros from './kpis/KpisSiniestros';
import KpisRenovaciones from './kpis/KpisRenovaciones';

// --- IMPORTACIÓN DEL COMPONENTE DE OBJETIVOS SANCOR ---
import KpisObjetivosSancor from './objetivosSancor/KpisObjetivosSancor';

// --- COMPONENTE DE SELECCIÓN DE RANGO DE FECHAS CON ATAJOS ---
import DateRangePicker from './common/DateRangePicker';

// --- CUSTOM HOOK DE LOGICA DE REPORTES ---
import { useReportesAvanzados } from './useReportesAvanzados';

// --- ICONOS SVG PREMIUM ---
const IconArrowLeft = () => <svg className="w-5 h-5 transition-transform group-hover:-translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/></svg>;
const IconDownload = () => <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/></svg>;
const IconDatabase = () => <svg className="w-12 h-12 text-[#01032d] opacity-90 stroke-[1.5]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/></svg>;
const IconSearch = () => <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>;
const IconChart = () => <svg className="w-4 h-4 transition-transform group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2zm2 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>;
const IconUser = () => <svg className="w-4 h-4 transition-transform group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>;
const IconTable = () => <svg className="w-4 h-4 transition-transform group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>;
const IconColumns = () => <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>;

// --- COMPONENTE INTERNO PREMIUM: POPOVER SELECTOR DE COLUMNAS ---
const ColumnSelectorPopover = ({ camposDisponibles, columnasSeleccionadas, setColumnasSeleccionadas }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [seccionAbierta, setSeccionAbierta] = useState(Object.keys(camposDisponibles)[0]);
    const popoverRef = useRef(null);

    // Cerrar si se da click fuera
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (popoverRef.current && !popoverRef.current.contains(e.target)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Filtra las columnas por la barra de búsqueda integrada
    const totalColumnasFiltradas = Object.entries(camposDisponibles).reduce((acc, [seccion, campos]) => {
        const filtered = campos.filter(c => c.label.toLowerCase().includes(searchQuery.toLowerCase()));
        if (filtered.length > 0) acc[seccion] = filtered;
        return acc;
    }, {});

    return (
        <div className="relative" ref={popoverRef}>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className={`flex items-center gap-2 px-4 py-2 text-xs font-bold uppercase rounded-xl border transition-all shadow-sm active:scale-95 ${
                    isOpen 
                        ? 'bg-[#01032d] text-white border-[#01032d]' 
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                }`}
            >
                <IconColumns />
                <span>Columnas</span>
                <span className="px-1.5 py-0.5 bg-slate-100 text-slate-700 text-[10px] font-black rounded-md group-hover:bg-slate-200">
                    {columnasSeleccionadas.length}
                </span>
            </button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-white border border-slate-200/90 rounded-2xl shadow-xl z-50 p-4 flex flex-col max-h-[480px] animate-scale-up">
                    <div className="flex items-center justify-between border-b pb-2 mb-3">
                        <h4 className="text-[11px] font-black text-[#01032d] uppercase tracking-wider">Configurar Columnas</h4>
                        <button 
                            type="button" 
                            onClick={() => setColumnasSeleccionadas([])} 
                            className="text-[10px] text-rose-500 font-extrabold hover:underline uppercase"
                        >
                            Limpiar Todo
                        </button>
                    </div>

                    {/* Buscador de Columnas */}
                    <input
                        type="text"
                        placeholder="Buscar columna..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2 text-[11px] mb-3 font-semibold outline-none focus:bg-white focus:border-blue-900 transition-colors"
                    />

                    {/* Acordeones de Secciones de Columnas */}
                    <div className="overflow-y-auto flex-grow space-y-1.5 pr-1 custom-scrollbar">
                        {Object.keys(totalColumnasFiltradas).length === 0 ? (
                            <p className="text-[11px] text-slate-400 italic text-center py-4">No se encontraron columnas</p>
                        ) : (
                            Object.entries(totalColumnasFiltradas).map(([seccionName, campos]) => {
                                const isSeccionOpen = seccionAbierta === seccionName || searchQuery !== '';
                                return (
                                    <div key={seccionName} className="border border-slate-100 rounded-xl overflow-hidden bg-slate-50/40">
                                        <button
                                            type="button"
                                            onClick={() => setSeccionAbierta(isSeccionOpen ? '' : seccionName)}
                                            className="w-full p-2.5 px-3 flex justify-between items-center text-[9px] font-black uppercase tracking-wider text-left bg-slate-100 hover:bg-slate-150 transition-colors"
                                        >
                                            <span className="text-slate-600 truncate">{seccionName}</span>
                                            <span className="text-[8px] text-slate-400">{isSeccionOpen ? '▲' : '▼'}</span>
                                        </button>
                                        
                                        {isSeccionOpen && (
                                            <div className="p-3 space-y-2 bg-white max-h-40 overflow-y-auto custom-scrollbar animate-fade-in">
                                                {campos.map(f => {
                                                    const isChecked = columnasSeleccionadas.includes(f.key);
                                                    return (
                                                        <label 
                                                            key={f.key} 
                                                            className={`flex items-center gap-2.5 p-1 px-1.5 hover:bg-slate-50 rounded-lg cursor-pointer text-xs font-bold transition-colors text-left ${
                                                                isChecked ? 'bg-blue-50/20 text-[#01032d]' : 'text-slate-500'
                                                            }`}
                                                        >
                                                            <input 
                                                                type="checkbox" 
                                                                checked={isChecked} 
                                                                onChange={() => setColumnasSeleccionadas(p => p.includes(f.key) ? p.filter(k => k !== f.key) : [...p, f.key])} 
                                                                className="w-4 h-4 rounded text-[#01032d] focus:ring-slate-500 border-slate-300 cursor-pointer" 
                                                            />
                                                            <span className="truncate">{f.label}</span>
                                                        </label>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const ReportesAvanzados = () => {
    const navigate = useNavigate();
    
    // Inyección de lógica pura optimizada del custom hook
    const {
        fechaDesde, setFechaDesde,
        fechaHasta, setFechaHasta,
        loadingProgress,
        isDbLoaded,
        queryError,
        activeTab, setActiveTab,
        moduloRaiz, setModuloRaiz,
        columnasSeleccionadas, setColumnasSeleccionadas,
        busquedaGlobal, setBusquedaGlobal,
        agruparPor, setAgruparPor,
        multiFiltros, setMultiFiltros,
        camposDisponibles,
        opcionesFiltrosUnicas,
        registrosProcesados,
        registrosAgrupados,
        consultarReportePorRango,
        exportarExcel
    } = useReportesAvanzados();

    // Detección automática del parámetro URL al ingresar desde ReportesHub
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const moduloURL = params.get('modulo');
        if (moduloURL) {
            setModuloRaiz(moduloURL);
        }
    }, [setModuloRaiz]);

    return (
        <div className="flex flex-col h-screen w-full bg-[#f8fafc] text-slate-800 overflow-hidden font-sans text-left animate-fade-in antialiased">
            {/* Línea decorativa sutil */}
            <div className="h-1 bg-[#01032d] w-full shrink-0"></div>

            {/* HEADER SUPERIOR CON SELECTOR DE RANGO INTEGRADO */}
            <header className="h-20 shrink-0 bg-white border-b border-slate-200/80 px-8 flex items-center justify-between z-40 relative shadow-[0_2px_15px_-3px_rgba(0,0,0,0.04)]">
                <div className="flex items-center gap-5">
                    <button 
                        onClick={() => navigate('/ReportesHub')} 
                        className="group p-2.5 text-slate-500 hover:text-slate-800 hover:bg-slate-50 rounded-xl border border-slate-200/60 shadow-sm transition-all duration-200 active:scale-95"
                        title="Volver al panel"
                    >
                        <IconArrowLeft />
                    </button>
                    <div>
                        <h2 className="text-[17px] font-black text-[#01032d] tracking-tight uppercase leading-none">
                            {moduloRaiz === 'OBJETIVOS_SANCOR' ? 'Desafío Comercial SANCOR 2026' : 'BI & Analítica de Negocio'}
                        </h2>
                        <p className="text-[10px] text-slate-400 font-extrabold uppercase tracking-widest mt-1.5 flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                            {moduloRaiz === 'OBJETIVOS_SANCOR' ? 'Monitoreo de Producción Neta vs Metas Cuatrimestrales' : 'Sincronización Relacional de Base de Datos'}
                        </p>
                    </div>
                </div>

                {/* SELECTOR INTEGRADO DE FECHAS (Sólo visible en módulos relacionales) */}
                {moduloRaiz !== 'OBJETIVOS_SANCOR' && (
                    <DateRangePicker 
                        fechaDesde={fechaDesde}
                        setFechaDesde={setFechaDesde}
                        fechaHasta={fechaHasta}
                        setFechaHasta={setFechaHasta}
                        onSubmit={consultarReportePorRango}
                    />
                )}

                {/* BOTÓN EXPORTAR EXCEL PREMIUM */}
                {moduloRaiz !== 'OBJETIVOS_SANCOR' && (
                    <button 
                        onClick={exportarExcel} 
                        disabled={registrosProcesados.length === 0} 
                        className="flex items-center px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:hover:bg-slate-300 disabled:bg-slate-100 disabled:text-slate-400 text-white rounded-xl font-bold text-xs uppercase tracking-wider transition-all duration-200 shadow-sm active:scale-[0.98]"
                    >
                        <IconDownload /> Exportar Excel
                    </button>
                )}
            </header>

            {/* CONTENEDOR PRINCIPAL */}
            <div className="flex-grow flex overflow-hidden">
                
                {/* FILTROS LATERALES REDISEÑADOS */}
                <FiltrosSidebar 
                    moduloRaiz={moduloRaiz}
                    setModuloRaiz={setModuloRaiz}
                    isDbLoaded={isDbLoaded}
                    opcionesFiltrosUnicas={opcionesFiltrosUnicas}
                    multiFiltros={multiFiltros}
                    setMultiFiltros={setMultiFiltros}
                    agruparPor={agruparPor}
                    setAgruparPor={setAgruparPor}
                />

                {/* AREA PRINCIPAL DE REPORTE */}
                <main className="flex-grow flex flex-col overflow-hidden bg-[#f1f5f9]">
                    
                    {/* SI ES EL MÓDULO DE OBJETIVOS SANCOR RENDERIZA EL DASHBOARD COMPLETO */}
                    {moduloRaiz === 'OBJETIVOS_SANCOR' ? (
                        <div className="flex-grow overflow-auto p-6 md:p-8">
                            <KpisObjetivosSancor />
                        </div>
                    ) : (
                        <>
                            {/* BARRA DE PESTAÑAS (TABS SEGMENTED CONTROL) */}
                            <div className="bg-white border-b border-slate-200/80 px-6 h-14 flex items-center justify-between shrink-0 shadow-[0_1px_3px_rgba(0,0,0,0.01)]">
                                <div className="flex bg-slate-100/80 p-1 rounded-xl">
                                    <button 
                                        onClick={() => setActiveTab('TABLA')} 
                                        className={`flex items-center gap-2 px-4 py-1.5 text-xs font-bold uppercase rounded-lg transition-all duration-200 ${activeTab === 'TABLA' ? 'bg-white text-[#01032d] shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                                    >
                                        <IconTable /> Vista Tabla
                                    </button>
                                    <button 
                                        onClick={() => setActiveTab('ESTADISTICAS')} 
                                        className={`flex items-center gap-2 px-4 py-1.5 text-xs font-bold uppercase rounded-lg transition-all duration-200 ${activeTab === 'ESTADISTICAS' ? 'bg-white text-[#01032d] shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                                    >
                                        <IconChart /> Estadísticas KPIs
                                    </button>
                                    <button 
                                        onClick={() => setActiveTab('VENDEDORES')} 
                                        className={`flex items-center gap-2 px-4 py-1.5 text-xs font-bold uppercase rounded-lg transition-all duration-200 ${activeTab === 'VENDEDORES' ? 'bg-white text-[#01032d] shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                                    >
                                        <IconUser /> {
                                            moduloRaiz === 'COBRANZA' 
                                                ? 'Análisis Cobradores' 
                                                : moduloRaiz === 'RENOVACIONES' 
                                                    ? 'Análisis Renovaciones' 
                                                    : 'Análisis Vendedores'
                                        }
                                    </button>
                                </div>

                                {/* ACCIONES DE TABLA: BUSCADOR & CONFIGURADOR FLOTANTE DE COLUMNAS */}
                                {isDbLoaded && activeTab === 'TABLA' && (
                                    <div className="flex items-center gap-3">
                                        <div className="relative w-80">
                                            <span className="absolute left-3.5 top-1/2 -translate-y-1/2"><IconSearch /></span>
                                            <input 
                                                type="text" 
                                                placeholder="Filtrar (Nombre, Póliza, CUIT)..." 
                                                value={busquedaGlobal} 
                                                onChange={(e) => setBusquedaGlobal(e.target.value)} 
                                                className="w-full bg-slate-50 text-xs border border-slate-200/80 rounded-xl pl-10 pr-4 py-2 outline-none font-medium transition-all focus:bg-white focus:ring-2 focus:ring-[#01032d]/10 focus:border-[#01032d]" 
                                            />
                                        </div>
                                        
                                        <ColumnSelectorPopover 
                                            camposDisponibles={camposDisponibles}
                                            columnasSeleccionadas={columnasSeleccionadas}
                                            setColumnasSeleccionadas={setColumnasSeleccionadas}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* VISTA Y CONTENIDO DE REPORTES */}
                            <div className="flex-grow overflow-auto p-6 md:p-8">
                                {queryError && (
                                    <div className="mb-6 p-4 bg-rose-50 text-rose-800 rounded-2xl border border-rose-100 text-xs font-medium leading-relaxed flex items-start gap-4 animate-fade-in shadow-sm">
                                        <span className="p-2 bg-rose-100 rounded-xl text-rose-600 shrink-0">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>
                                        </span>
                                        <div className="space-y-1">
                                            <p className="uppercase tracking-wider font-extrabold text-rose-900">Requiere Índice en Firestore</p>
                                            <p className="text-slate-600">
                                                Es probable que necesites habilitar un índice compuesto para este filtro de fechas. Abre la consola del desarrollador (F12) y haz clic en el enlace de creación automática generado por Firebase.
                                            </p>
                                            <p className="text-[10px] text-rose-500 font-mono mt-1 bg-rose-100/50 py-1 px-2.5 rounded-lg inline-block">Detalle: {queryError}</p>
                                        </div>
                                    </div>
                                )}

                                {isDbLoaded ? (
                                    <>
                                        {/* TAB 1: PREVISUALIZACIÓN DE TABLA */}
                                        {activeTab === 'TABLA' && (
                                            agruparPor !== 'NINGUNO' && registrosAgrupados ? (
                                                <GroupedPreview 
                                                    registrosAgrupados={registrosAgrupados}
                                                    columnasSeleccionadas={columnasSeleccionadas}
                                                    camposDisponibles={camposDisponibles}
                                                    moduloRaiz={moduloRaiz}
                                                    agruparPor={agruparPor}
                                                />
                                            ) : (
                                                <TablePreview 
                                                    registros={registrosProcesados}
                                                    columnasSeleccionadas={columnasSeleccionadas}
                                                    camposDisponibles={camposDisponibles}
                                                    moduloRaiz={moduloRaiz}
                                                />
                                            )
                                        )}

                                        {/* TAB 2: KPIs DINÁMICOS */}
                                        {activeTab === 'ESTADISTICAS' && (
                                            <div className="animate-fade-in">
                                                {moduloRaiz === 'POLIZAS' && <KpisPolizas registros={registrosProcesados} isDbLoaded={isDbLoaded} />}
                                                {moduloRaiz === 'COBRANZA' && <KpisCobranzas registros={registrosProcesados} isDbLoaded={isDbLoaded} />}
                                                {moduloRaiz === 'ENDOSOS' && <KpisEndosos registros={registrosProcesados} isDbLoaded={isDbLoaded} />}
                                                {moduloRaiz === 'REFACTURACIONES' && <KpisRefacturaciones registros={registrosProcesados} isDbLoaded={isDbLoaded} />}
                                                {moduloRaiz === 'SINIESTROS' && <KpisSiniestros registros={registrosProcesados} isDbLoaded={isDbLoaded} />}
                                                {moduloRaiz === 'ANULACIONES' && <KpisAnulaciones registros={registrosProcesados} isDbLoaded={isDbLoaded} />}
                                                {moduloRaiz === 'RENOVACIONES' && <KpisRenovaciones registros={registrosProcesados} isDbLoaded={isDbLoaded} />}
                                            </div>
                                        )}

                                        {/* TAB 3: RENDIMIENTO SEGÚN MÓDULO (COBRANZA / RENOVACIONES / POLIZAS) */}
                                        {activeTab === 'VENDEDORES' && (
                                            moduloRaiz === 'COBRANZA' ? (
                                                <RendimientoCobradores 
                                                    registros={registrosProcesados}
                                                    isDbLoaded={isDbLoaded}
                                                    porcentajeIncentivo={0.0}
                                                />
                                            ) : moduloRaiz === 'RENOVACIONES' ? (
                                                <RendimientoRenovacionesVendedores 
                                                    registros={registrosProcesados}
                                                    isDbLoaded={isDbLoaded}
                                                    porcentajeIncentivo={0.4}
                                                />
                                            ) : (
                                                <RendimientoVendedores 
                                                    registros={registrosProcesados}
                                                    isDbLoaded={isDbLoaded}
                                                />
                                            )
                                        )}
                                    </>
                                ) : (
                                    /* EMPTY STATE */
                                    <div className="flex flex-col items-center justify-center p-12 text-center bg-white border border-slate-200/60 rounded-3xl max-w-xl mx-auto mt-16 shadow-[0_4px_25px_-5px_rgba(0,0,0,0.02)]">
                                        <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl mb-5 shadow-sm">
                                            <IconDatabase />
                                        </div>
                                        <h3 className="text-[15px] font-black uppercase tracking-wider text-slate-800">Centro de Inteligencia de BI</h3>
                                        <p className="text-xs text-slate-400 max-w-sm mt-2 leading-relaxed">
                                            Selecciona el rango de fechas en la parte superior y haz clic en <span className="font-semibold text-slate-600">"Cargar Rango"</span> para estructurar las vigencias y cruces operacionales.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </main>
            </div>

            {/* MODAL DE PROGRESO DE CARGA */}
            {loadingProgress.active && (
                <div className="fixed inset-0 bg-[#0c0f24]/60 z-[2000] flex items-center justify-center p-4 backdrop-blur-md animate-fade-in">
                    <div className="bg-white rounded-[28px] p-8 max-w-md w-full border border-slate-200/50 text-center space-y-6 shadow-[0_20px_50px_rgba(0,0,0,0.3)] animate-scale-up">
                        <div className="relative w-16 h-16 mx-auto flex items-center justify-center">
                            <div className="absolute inset-0 border-4 border-slate-100 rounded-full"></div>
                            <div className="absolute inset-0 border-4 border-t-[#01032d] border-r-[#01032d] rounded-full animate-spin"></div>
                        </div>
                        
                        <div className="space-y-1.5">
                            <p className="text-[10px] font-black text-[#01032d] uppercase tracking-widest bg-slate-100/80 px-3 py-1 rounded-full inline-block">
                                Paso {loadingProgress.step} de 6
                            </p>
                            <p className="text-[15px] font-extrabold text-slate-800">{loadingProgress.text}</p>
                        </div>
                        
                        <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden shadow-inner p-0.5">
                            <div 
                                className="bg-[#01032d] h-full rounded-full transition-all duration-300 ease-out" 
                                style={{ width: `${(loadingProgress.step / 6) * 100}%` }}
                            ></div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ReportesAvanzados;